/**
 * The vibe coding panels: eight terminal moments, drawn as live DOM.
 *
 * WHY THIS EXISTS. Corridor v2 (sv-cloud-plan/landing-concept/07-corridor-v2-spec.md,
 * founder ruling 21 Aug 2026) pulled every face out of the flight and replaced
 * those cards with the thing the product is actually about, in our own terminal
 * idiom, animated. The contract for what a panel is lives in src/lib/panels.ts,
 * on purpose and apart from this file, so the manifest that schedules panels
 * does not pull this animation code into its chunk.
 *
 * WHY DOM RATHER THAN VIDEO OR AN IMAGE, restated because it is the reason
 * every decision below goes the way it does. A panel inherits the brand tokens,
 * so a direction or a theme flip restyles all eight with no re-export. It stays
 * crisp as a card grows from thumbnail to full bleed. Twelve video loops would
 * outweigh the rest of the page. And it is honest: these are the product's real
 * words and the product's real vocabulary, so the flight is evidence rather
 * than decoration.
 *
 * THE `active` PROP IS THE WHOLE PERFORMANCE BUDGET. Only the two or three
 * cards nearest the camera animate their contents, because at the far end the
 * motion is invisible anyway and eighteen animating panels is not a corridor,
 * it is a heater. A panel that is not active renders its SETTLED FINAL FRAME:
 * the sentence finished typing, the bar is full, every row has arrived. Far
 * cards have to look finished rather than empty or caught mid stride, and that
 * is what makes the corridor read as a real place rather than a slideshow of
 * loading states.
 *
 * HOW THAT IS DONE, and it is one rule rather than a second render path: the
 * base stylesheet IS the settled frame, and every animation is a `from`
 * keyframe attached only under [data-active='yes']. Turn the animations off,
 * by flipping the attribute or by asking for reduced motion, and what is left
 * standing is the finished picture. There is no "final frame" branch in this
 * file to keep in step with the animated one.
 *
 * NO TIMERS, NO STATE, NO EFFECTS. Every beat is a CSS animation with a delay,
 * and character reveal is steps() on a clip, so the compositor does the work
 * and a panel costs one style recalc per beat on a contained subtree. The brief
 * allows one JS timer per panel; none of the eight needs one. Flipping `active`
 * from false to true attaches the animation names and the run starts fresh,
 * which means the corridor can replay a panel every time a card comes round
 * without this component knowing the corridor exists.
 *
 * IT RUNS ONCE AND RESTS, like the corridor itself. A visible loop reclassifies
 * a title sequence as a screensaver, and the same argument that stops the
 * flight on a composed frame stops these panels on theirs. Nothing in the eight
 * loops, including the caret: the log tail's caret earns its blink because it
 * marks a stream that is still arriving, and a sentence that has landed is not
 * arriving. The caret's POSITION is the information, and it holds.
 *
 * THE COLOUR RULE, and it is the strongest visual rule in this direction: THE
 * ONLY SATURATED PIXELS IN THE ENTIRE FLIGHT ARE THE STATUS LAMPS. A green lamp
 * in panel-lamp and panel-ship is the point. A green success bar, a green tick
 * on a finished build step, a red error fence: none of those. Every tick, every
 * bar, every fence and every window dot in here is neutral ink, which is why a
 * single green disc four cards away still catches the eye.
 *
 * WHY THE LAMPS ARE REDRAWN HERE INSTEAD OF IMPORTING StatusPill's Lamp. That
 * component sizes its svg in fixed pixels (8, 12 and 40), which is correct on a
 * console screen and wrong inside a card that is laid out at one width and then
 * scaled by a 3D transform. The shapes and the tone mapping below are the
 * house's, copied deliberately so the corridor and the console agree about what
 * live looks like; only the sizing changed.
 *
 * It is decoration. aria-hidden, no focus stop, nothing announced.
 */

import type { CSSProperties, ReactNode } from 'react';

import { t } from '../../lib/i18n/t';
import type { PanelId } from '../../lib/panels';

import '../../styles/components/terminal-panel.css';

/* --------------------------------------------------------------- geometry */

/**
 * Characters that fit across the body at the panel's own type scale.
 *
 * It is a derived number, not a taste: terminal-panel.css sets the body type at
 * 5cqw inside 4cqw of padding on each side, JetBrains Mono advances 0.6em per
 * glyph, so the line holds 92 / 3, or thirty characters. The prompt spends
 * three of them on its sigil column, which leaves twenty seven. Change the type
 * scale and this has to move with it.
 */
const COLS = 27;

/** Milliseconds per revealed character. Reading pace, not typing pace. */
const CHAR = 55;

/** Milliseconds between two arrivals in a list. */
const BEAT = 380;

/**
 * The address the hero types in chapter 1, repeated here on purpose so a
 * visitor who reads the corridor closely is handed the same promise twice.
 *
 * It is a literal rather than an import of PUBLIC_DOMAIN because this file
 * flies in the corridor chunk and that constant lives in the mock layer, which
 * has no business being pulled into a marketing animation. If the public domain
 * ever changes, this line and Landing.tsx's HERO_ADDRESS move together.
 */
const HERO_ADDRESS = 'nina-recipes.apps.sv-academy.org';

/**
 * Machine facts stay literal, exactly as a commit SHA and a URL do everywhere
 * else in this product. A file path and a hash are not translated, and routing
 * them through t() would put a line in the catalogue that no translator can do
 * anything useful with.
 */
const ERROR_WHERE = 'app/checkout.tsx:42';
const COMMIT = 'a4f19c2';
const BRANCH = 'main';

/** The six tables panel-rows fills in. Names and counts are machine facts. */
const TABLES: readonly { name: string; rows: number }[] = [
  { name: 'recipes', rows: 128 },
  { name: 'ingredients', rows: 942 },
  { name: 'steps', rows: 604 },
  { name: 'users', rows: 37 },
  { name: 'favorites', rows: 210 },
  { name: 'searches', rows: 88 },
];

/* ---------------------------------------------------------------- plumbing */

/**
 * One inline style for one cued element.
 *
 * THE INLINE STYLE RULE, which is the corridor's and applies here unchanged: an
 * inline style may carry a number, a length, a duration or a keyframe name, and
 * the moment a property paints it belongs in the stylesheet where check-tokens
 * and a reviewer can both see it. Nothing this function returns paints. The
 * keyframe NAMES stay in the stylesheet too, attached under [data-active='yes'],
 * because that attachment is what makes a far card render settled.
 *
 * An element that the stylesheet gives a name to but that never receives a
 * duration from here runs for 0s and lands on its `to` values, which are the
 * settled frame. That is a deliberate safety property rather than an accident:
 * forgetting a cue makes a panel look finished, never broken.
 */
function cue(at: number, duration: number, steps?: number, chars?: number): CSSProperties {
  const style: Record<string, string | number> = {
    animationDelay: `${at}ms`,
    animationDuration: `${duration}ms`,
  };
  if (steps !== undefined) style.animationTimingFunction = `steps(${steps})`;
  if (chars !== undefined) style['--n'] = chars;
  return style as unknown as CSSProperties;
}

/**
 * Wrap a sentence to a character budget, because the reveal is a clip on one
 * line and a clip cannot follow a browser's own line break.
 *
 * A language that does not break on spaces (Thai) comes back as one long line.
 * That is the honest outcome rather than a hidden one: the panel clips it, the
 * sentence still types, and the real fix is a Thai draft written to the width
 * rather than an algorithm guessing where a Thai clause ends.
 */
function wrap(text: string, cols: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    if (line === '') {
      line = word;
    } else if (line.length + 1 + word.length <= cols) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line !== '') lines.push(line);
  return lines;
}

/**
 * Split a counted string around its placeholder, so the number can be a live
 * element instead of a baked substring.
 *
 * The key still reaches t() whole, with the placeholder intact, so the
 * catalogue Charlie inherits carries "{n} tables ready" rather than two
 * fragments nobody can translate.
 */
function around(key: string): { before: string; after: string } {
  const [before = '', after = ''] = t(key).split('{n}');
  return { before, after };
}

/* -------------------------------------------------------------- the pieces */

/**
 * A sentence that types itself, one clip per line, with a caret riding the
 * clip edge.
 *
 * The clip and the caret share a delay, a duration and a steps() count, so they
 * cannot drift apart by a character. Both animate a compositor property
 * (clip-path and transform) rather than a width, which is the difference
 * between a paint per character and a layout per character.
 */
function Typed({ text, at }: { text: string; at: number }) {
  const lines = wrap(text, COLS).filter((line) => line.length > 0);
  let start = at;

  return (
    <span className="tpanel__typed">
      {lines.map((line, index) => {
        const delay = start;
        const duration = line.length * CHAR;
        start += duration;
        const last = index === lines.length - 1;
        const timing = cue(delay, duration, line.length, line.length);

        return (
          <span className="tpanel__line" key={`${index}-${line}`} style={timing}>
            <span className="tpanel__ink" style={timing}>
              {line}
            </span>
            {/* One caret per line, lit only while that line is being typed. The
                last one is cued to hold, because the caret at rest is the thing
                that says the prompt is still waiting for you. */}
            <i className="tpanel__caret" data-cue={last ? 'hold' : undefined} style={timing} />
          </span>
        );
      })}
    </span>
  );
}

/**
 * A plan item or a build step: it arrives, then its pending dot flips to a
 * tick. The tick is ink, never green. See the colour rule at the top.
 */
function Step({ label, arrive, tick }: { label: string; arrive: number; tick: number }) {
  return (
    <li className="tpanel__step" style={cue(arrive, 260)}>
      <span className="tpanel__glyph" aria-hidden="true">
        <span className="tpanel__dot" style={cue(tick, 160)} />
        <svg className="tpanel__check" viewBox="0 0 16 16" style={cue(tick, 160)} focusable="false">
          <path
            d="M3 8.5 6.5 12 13 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="tpanel__label">{label}</span>
    </li>
  );
}

/**
 * A number that climbs, as a reel of digits behind a one line window.
 *
 * steps() on a transform, so the count advances in whole numbers on the same
 * beat the rows land on and never shows a half digit. The settled frame is the
 * reel already parked on its last value, which is why the base transform in the
 * stylesheet is the end of the run rather than the start of it.
 */
function Odometer({ from, to, at, step }: { from: number; to: number; at: number; step: number }) {
  const values: number[] = [];
  for (let value = from; value <= to; value += 1) values.push(value);
  const last = Math.max(values.length - 1, 0);

  const style: Record<string, string | number> = {
    ...(cue(at, last * step, Math.max(last, 1)) as unknown as Record<string, string | number>),
    '--k': last,
  };

  return (
    <span className="tpanel__odo" aria-hidden="true">
      <span className="tpanel__reel" style={style as unknown as CSSProperties}>
        {values.map((value) => (
          <span className="tpanel__digit" key={value}>
            {value}
          </span>
        ))}
      </span>
    </span>
  );
}

/** The four house lamp shapes, at panel scale. Sized by CSS, in cqw. */
type LampShape = 'disc' | 'bullseye' | 'ring';
type LampTone = 'live' | 'attention' | 'building';

function PanelLamp({ shape, tone }: { shape: LampShape; tone: LampTone }) {
  return (
    <span className="tpanel__lamp" data-tone={tone} aria-hidden="true">
      <svg viewBox="0 0 16 16" focusable="false">
        {shape === 'disc' ? <circle cx="8" cy="8" r="7" className="tpanel__lamp-fill" /> : null}
        {shape === 'bullseye' ? (
          <path
            className="tpanel__lamp-fill"
            fillRule="evenodd"
            d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1Zm0 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
          />
        ) : null}
        {shape === 'ring' ? (
          <circle cx="8" cy="8" r="6" className="tpanel__lamp-ring" fill="none" />
        ) : null}
      </svg>
    </span>
  );
}

/** One state of the health band. Three of these stack and take turns. */
function Band({
  shape,
  tone,
  word,
  say,
  at,
  hold,
}: {
  shape: LampShape;
  tone: LampTone;
  word: string;
  say: string;
  at: number;
  hold?: boolean;
}) {
  return (
    <div
      className="tpanel__band"
      data-cue={hold ? 'hold' : undefined}
      style={cue(at, hold ? 520 : 900)}
    >
      <div className="tpanel__band-head">
        <PanelLamp shape={shape} tone={tone} />
        {/* The console's traffic band tints this word with the lamp colour. It
            stays neutral here, deliberately: in the flight a panel is a picture
            among fifteen others, and the budget is one saturated object per
            card so the lamp itself is the thing that carries across the room. */}
        <span className="tpanel__word">{word}</span>
      </div>
      <p className="tpanel__say">{say}</p>
    </div>
  );
}

/* ---------------------------------------------------------- the eight bodies */

/* Every t() call below runs at render rather than at module scope. A module
   scope t() would freeze the English into the bundle and a Thai visitor would
   never see the catalogue, which is the same rule streamManifest.ts states for
   its alt text. */

function PromptBody() {
  return (
    <>
      <p className="tpanel__note" style={cue(0, 300)}>
        {t('what do you want to build?')}
      </p>
      <p className="tpanel__prompt">
        <span className="tpanel__sigil" aria-hidden="true">
          {'>'}
        </span>
        <Typed text={t('a recipe site with photos and a search box')} at={420} />
      </p>
    </>
  );
}

function AgentBody() {
  return (
    <>
      <p className="tpanel__note" style={cue(0, 300)}>
        {t('here is my plan')}
      </p>
      <ul className="tpanel__steps">
        <Step label={t('reading your prompt')} arrive={300} tick={900} />
        <Step label={t('writing the database schema')} arrive={300 + BEAT} tick={1560} />
        <Step label={t('wiring up the search box')} arrive={300 + BEAT * 2} tick={2220} />
      </ul>
    </>
  );
}

function BuildBody() {
  return (
    <>
      <p className="tpanel__note" style={cue(0, 300)}>
        {t('building your app')}
      </p>
      <ul className="tpanel__steps">
        <Step label={t('installing packages')} arrive={300} tick={900} />
        <Step label={t('compiling your code')} arrive={300 + BEAT} tick={1600} />
        <Step label={t('making it small')} arrive={300 + BEAT * 2} tick={2300} />
      </ul>
      {/* The bar advances in three steps rather than sweeping, so it lands on
          the same frame each named step ticks. A bar that glides past its own
          steps is telling a different story from the list beside it. It is the
          neutral in flight token, never a status hue: coloured motion reads as
          an alarm and a build that is going fine is not an alarm. */}
      <span className="tpanel__track" aria-hidden="true">
        <span className="tpanel__fill" style={cue(200, 2100, 3)} />
      </span>
    </>
  );
}

function DeployBody() {
  const log = [
    t('uploading your build'),
    t('starting your app'),
    t('checking that it answers'),
    t('pointing the address at it'),
  ];

  return (
    <>
      <ul className="tpanel__log">
        {log.map((line, index) => (
          <li className="tpanel__log-line" key={line} style={cue(index * BEAT, 260)}>
            {line}
          </li>
        ))}
      </ul>
      <div className="tpanel__land" style={cue(1720, 460)}>
        <p className="tpanel__url">{HERO_ADDRESS}</p>
        <p className="tpanel__say">{t('your app is live')}</p>
      </div>
    </>
  );
}

function FixBody() {
  return (
    <>
      <div className="tpanel__fence">
        <p className="tpanel__fence-line" style={cue(0, 260)}>
          {t('formatCurency is not defined')}
        </p>
        <p className="tpanel__fence-line" style={cue(240, 260)}>
          {ERROR_WHERE}
        </p>
      </div>
      {/* The fix request arrives and then lifts a rung, which is the whole of
          the highlight. A tinted card would be a second chromatic object on a
          page whose entire argument is that colour means state. */}
      <div className="tpanel__fix" style={cue(800, 1200)}>
        <p className="tpanel__note">{t('copy this to your agent')}</p>
        <p className="tpanel__label">{t('one file spells it two ways')}</p>
        {/* The most valuable sentence in a fix request, and the one only the
            platform knows to say. It is the ErrorBlock's own closing line. */}
        <p className="tpanel__label">{t('do not deploy it yet')}</p>
      </div>
    </>
  );
}

function RowsBody() {
  const counted = around('{n} tables ready');

  return (
    <>
      <div className="tpanel__thead" style={cue(0, 260)}>
        <span>{t('table')}</span>
        <span className="tpanel__num">{t('rows')}</span>
      </div>
      <ul className="tpanel__rows">
        {TABLES.map((table, index) => (
          <li className="tpanel__row" key={table.name} style={cue(300 + index * 260, 240)}>
            <span>{table.name}</span>
            <span className="tpanel__num">{table.rows}</span>
          </li>
        ))}
      </ul>
      {/* The count is cued one beat early so its step lands on the same frame
          as the row it is counting, rather than a beat behind it. */}
      <p className="tpanel__count" style={cue(300, 260)}>
        {counted.before}
        <Odometer from={0} to={TABLES.length} at={40} step={260} />
        {counted.after}
      </p>
    </>
  );
}

function LampBody() {
  return (
    <>
      <p className="tpanel__note" style={cue(0, 300)}>
        {t('your app right now')}
      </p>
      <div className="tpanel__bands">
        <Band
          shape="ring"
          tone="building"
          word={t('working')}
          say={t('a deploy is going out')}
          at={300}
        />
        <Band
          shape="bullseye"
          tone="attention"
          word={t('needs you')}
          say={t('one thing needs a look')}
          at={1200}
        />
        {/* The band the panel rests on, and the one a far card shows. The three
            states are the three verdicts a person meets in a week, in the order
            they meet them, ending on the one that is true almost always. */}
        <Band
          shape="disc"
          tone="live"
          word={t('live')}
          say={t('everything is fine')}
          at={2100}
          hold
        />
      </div>
    </>
  );
}

function ShipBody() {
  const ago = around('deployed {n} seconds ago');

  return (
    <>
      <p className="tpanel__hash" style={cue(0, 260)}>
        <span>{COMMIT}</span>
        <span className="tpanel__branch">{BRANCH}</span>
      </p>
      <div className="tpanel__state" style={cue(460, 260)}>
        <PanelLamp shape="disc" tone="live" />
        <span className="tpanel__word">{t('live')}</span>
      </div>
      <p className="tpanel__url" style={cue(640, 260)}>
        {HERO_ADDRESS}
      </p>
      <p className="tpanel__ago" style={cue(820, 260)}>
        {ago.before}
        {/* It starts at two, not at one. The line is written with a plural, so
            a reel that passes through 1 prints "1 seconds ago" for six hundred
            milliseconds on its way to the frame the panel rests on. Two is the
            first number the sentence is true for. */}
        <Odometer from={2} to={4} at={820} step={620} />
        {ago.after}
      </p>
    </>
  );
}

/* ------------------------------------------------------------- the component */

/**
 * The chrome title, per panel. English strings, filed the way StatusPill files
 * its lamp words: the map holds the key and t() runs at render.
 *
 * They are lowercase because a terminal tab is lowercase, and they name the
 * moment rather than the feature.
 */
const TITLE: Record<PanelId, string> = {
  'panel-prompt': 'new project',
  'panel-agent': 'agent',
  'panel-build': 'build',
  'panel-deploy': 'deploy',
  'panel-fix': "didn't work",
  'panel-rows': 'database',
  'panel-lamp': 'health',
  'panel-ship': 'deploys',
};

/**
 * A Record rather than a switch, so adding a ninth panel id to the contract in
 * src/lib/panels.ts fails this file at compile time instead of falling through
 * to an empty card at runtime.
 */
const BODY: Record<PanelId, () => ReactNode> = {
  'panel-prompt': PromptBody,
  'panel-agent': AgentBody,
  'panel-build': BuildBody,
  'panel-deploy': DeployBody,
  'panel-fix': FixBody,
  'panel-rows': RowsBody,
  'panel-lamp': LampBody,
  'panel-ship': ShipBody,
};

export interface TerminalPanelProps {
  /** Which of the eight. The union lives in src/lib/panels.ts. */
  id: PanelId;
  /**
   * True when this card is near the camera and should animate its contents.
   * False renders the settled final frame, which is what every far card shows.
   * Flipping false to true replays the run from the top.
   */
  active: boolean;
}

export function TerminalPanel({ id, active }: TerminalPanelProps) {
  const Body = BODY[id];

  return (
    <div className="tpanel" data-panel={id} data-active={active ? 'yes' : 'no'} aria-hidden="true">
      {/* The frame, and not a wrapper for the sake of one. .tpanel carries
          container-type, and an element cannot query itself: cqw inside
          .tpanel's own declarations would resolve against the corridor, not
          against the card. Every cqw length therefore lives from here down. */}
      <div className="tpanel__frame">
        <div className="tpanel__chrome">
          {/* Window dots. Neutral, and it costs something to say no here: the
              official SV terminal tile carries three coloured traffic dots.
              Three saturated specks of furniture on every panel would spend the
              flight's entire colour budget on chrome, and then the one green
              lamp that means something would have to compete with them. */}
          <span className="tpanel__dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="tpanel__title">{t(TITLE[id])}</span>
        </div>

        <div className="tpanel__body">
          <Body />
        </div>
      </div>
    </div>
  );
}

export default TerminalPanel;
